import { ReactElement } from 'react'
import clsx from 'clsx'

import { CLI_INSTALL_URL, PAGE_ROUTES } from '~/constants'
import Text from '~/components/Text/Text'
import Link from '~/components/Link/Link'
import Button from '~/components/Button/Button'
import InstallationSelect from '~/components/InstallationSelect/InstallationSelect'
import CodeInline from '~/components/CodeInline/CodeInline'

import styles from './CLIEntry.module.sass'

const listItems: (string | ReactElement)[] = [
  'Expressive and intuitive syntax',
  'First-class JSON support',
  'Colorized and formatted terminal output',
  'Sensible defaults for the API era',
  'Persistent sessions',
  'Forms and file uploads',
  'HTTPS, proxies, and authentication support',
  'Support for arbitrary request data and headers',
  <>
    <CodeInline>wget</CodeInline>-like download mode
  </>,
  'Extensions API for authentication, etc.',
  <>
    All that & more in two simple <CodeInline>http</CodeInline> + <CodeInline>https</CodeInline> commands
  </>,
]

const CLIEntry = () => (
  <div className={styles.section}>
    <div className='site-container site-grid'>
      <div className='flex justify-start col-span-2 md:col-span-4 lg:col-start-2'>
        <img src='/Images/cli-comparison.png' alt='HTTPie demo' className={styles.comparisonImage} />
      </div>

      <div className='col-span-full md:col-span-4 lg:col-span-5 lg:col-start-7'>
        <Text variant='subheading' fontSize={[28, 28, 40]} className={styles.heading}>
          Designed for APIs
        </Text>
        <Text fontSize={[18, 14, 18]} className={styles.description}>
          We built HTTPIe from the ground up for painless testing and debugging of APIs, HTTP servers, and web services.
        </Text>

        <ul className={clsx('dotted-list', styles.list)}>
          {listItems.map((item, index) => (
            <li key={index} className={styles.listItem}>
              <Text fontSize={[18, 14, 18]} as='span'>
                {item}
              </Text>
            </li>
          ))}
        </ul>
        <Button
          link={PAGE_ROUTES.DOCS_CLI}
          className={clsx(styles.button, styles.seeAllFeaturesButton)}
          data-track='cta:see-all-features'
        >
          See all features
        </Button>
      </div>

      <div
        id='installation'
        className={clsx(
          'col-span-full md:col-span-4 md:row-start-2 md:col-start-2 lg:col-start-4',
          styles.installation
        )}
      >
        <div>
          <Text variant='subheading' fontSize={[28, 28, 40]} className={styles.installationHeading}>
            Installation
          </Text>
          <Text variant='body' fontSize={[18, 14, 18]} className={styles.installationDescription}>
            HTTPie CLI can be installed on any OS.
          </Text>
        </div>

        <InstallationSelect className={styles.installationSelect} />

        <div className={styles.installationButtons}>
          <Button
            link={CLI_INSTALL_URL}
            className={clsx(styles.button, 'mr-3')}
            data-track='cta:learn-more-about-cli-installation'
          >
            Learn more
          </Button>

          <Text fontSize={15} className={clsx(styles.appSectionLink)}>
            <Link
              href={PAGE_ROUTES.RUN}
              className={styles.link}
              data-track='cta:try-online:from-cli-installation'
              underlined
            >
              Try Online
            </Link>
          </Text>
        </div>
      </div>
    </div>
  </div>
)

export default CLIEntry
